import React, { useCallback, useEffect } from 'react';
import type { ExecutionResult } from '@apollo/react-common';
import { Text } from '@atlaskit/primitives';
import { useRefetchFields } from '@atlassian/jira-business-entity-project/src/controllers/refetch-fields/index.tsx';
import { isFieldInResponse } from '@atlassian/jira-business-entity-project/src/controllers/refetch-fields/utils.tsx';
import { useFlagsService, toFlagId } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useAssociateField as useAssociateFieldService } from '../../services/associate-field';
import type { JwmAssociateField } from '../../services/associate-field/__generated_apollo__/JwmAssociateField';
import { useAssociateFieldStore, type AssociateFieldProps } from './associate-field-store';
import messages from './messages';

export const ASSOCIATE_FIELD_ERROR_FLAG_ID = toFlagId('associate-field-error-flag');
export const ASSOCIATE_FIELD_SUCCESS_FLAG_ID = toFlagId('associate-field-success-flag');
export const REFETCH_FIELDS_ERROR_FLAG_ID = toFlagId('refetch-fields-error-flag');

export type AssociateFieldReturn = ExecutionResult<JwmAssociateField>;

export const useAssociateField = () => {
	const { showFlag, dismissFlag } = useFlagsService();
	const { associate, loading, error } = useAssociateFieldService();
	const { refetchFields } = useRefetchFields();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// the BE has a limitation where it cannot handle multiple associate calls in flight so we need a queue
	const [
		{ associateQueue },
		{ setAssociateQueue, associate: associateStore, addToAssociateQueue },
	] = useAssociateFieldStore();

	const associateField = useCallback(
		({
			input,
			fieldName,
			issueType,
			showSuccess = false,
			analyticsAttributes,
		}: AssociateFieldProps) =>
			new Promise<AssociateFieldReturn>((resolve, reject) => {
				addToAssociateQueue({
					resolve,
					reject,
					fieldProps: {
						input,
						fieldName,
						issueType,
						showSuccess,
						analyticsAttributes,
					},
				});
			}),
		[addToAssociateQueue],
	);

	const associateNextInQueue = useCallback(async () => {
		if (associateQueue.length === 0) {
			return;
		}
		const [
			{
				resolve,
				reject,
				fieldProps: { input, fieldName, issueType, showSuccess, analyticsAttributes = {} },
			},
		] = associateQueue;
		setAssociateQueue(associateQueue.splice(1)); // returns new array by keeping the first one only

		dismissFlag(`${ASSOCIATE_FIELD_ERROR_FLAG_ID}-${input.fieldId}`);
		dismissFlag(`${REFETCH_FIELDS_ERROR_FLAG_ID}-${input.fieldId}`);

		const result = await associate({
			input,
		});

		if (!result?.data?.jira?.jwmAssociateField?.success) {
			showFlag({
				id: `${ASSOCIATE_FIELD_ERROR_FLAG_ID}-${input.fieldId}`,
				type: 'error',
				title: formatMessage(messages.associateFieldErrorTitleV2, { fieldName }),
				description: messages.associateFieldErrorMessageV2,
				actions: [
					{
						content: messages.associateFieldTryAgainAction,
						onClick: () => {
							associateField({
								input,
								fieldName,
								issueType,
								showSuccess,
								analyticsAttributes,
							});
						},
					},
				],
			});

			fireTrackAnalytics(createAnalyticsEvent({}), 'associate failed', analyticsAttributes);
			reject(new Error('Associate field mutation did not succeed'));
			return;
		}

		fireTrackAnalytics(createAnalyticsEvent({}), 'field associated', analyticsAttributes);

		if (showSuccess) {
			showFlag({
				id: ASSOCIATE_FIELD_SUCCESS_FLAG_ID,
				type: 'success',
				title: messages.successTitle,
				description: [
					messages.successDescription,
					{
						fieldName,
						issueType,
						strong: (chunks: string) => <Text as="strong">{chunks}</Text>,
					},
				],
			});
		}

		try {
			await refetchFields({
				shouldRetry: ([response]) => !isFieldInResponse({ response, fieldId: input.fieldId }),
			});
		} catch (e) {
			showFlag({
				id: `${REFETCH_FIELDS_ERROR_FLAG_ID}-${input.fieldId}`,
				type: 'error',
				title: formatMessage(messages.refetchFieldsErrorTitle, { fieldName }),
				description: messages.refetchFieldsErrorMessage,
			});

			reject(e);
			return;
		}

		resolve(result);
	}, [
		associateQueue,
		setAssociateQueue,
		dismissFlag,
		associate,
		createAnalyticsEvent,
		showFlag,
		formatMessage,
		associateField,
		refetchFields,
	]);

	useEffect(() => {
		if (associateQueue.length > 0) {
			associateStore(associateNextInQueue);
		}
	}, [associateQueue, associateNextInQueue, associateStore]);

	return { associate: associateField, loading, error };
};
